import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getPropertyCoordinates, DEFAULT_CENTER, CITY_COORDINATES } from '../data/geocoding';
import { MapPin, Navigation, Compass, Sliders, ChevronRight, Home as HomeIcon, Building2, BedDouble, Maximize2, Euro, Search, X } from 'lucide-react';
import { formatPrice } from '../constants';
import { useSearch } from '../context/SearchContext';

// Liste des centres prédéfinis pour la recherche par rayon
const CENTER_OPTIONS = [
    { name: "Siège d'Ottange (57840)", coords: [49.4443, 6.0195] },
    { name: "Audun-le-Tiche (57390)", coords: [49.4725, 5.9572] },
    { name: "Aumetz (57580)", coords: [49.4181, 5.9439] },
    { name: "Boulange (57655)", coords: [49.3828, 6.0022] },
    { name: "Dudelange (Luxembourg)", coords: [49.4800, 6.0833] },
    { name: "Esch-sur-Alzette (Luxembourg)", coords: [49.4975, 5.9819] },
    { name: "Fontoy (57450)", coords: [49.3567, 5.9961] },
    { name: "Hayange (57700)", coords: [49.3297, 6.0622] },
    { name: "Longwy (54400)", coords: [49.5204, 5.7602] },
    { name: "Mercy-le-Bas (54960)", coords: [49.3811, 5.7603] },
    { name: "Mont-Saint-Martin (54350)", coords: [49.5407, 5.7801] },
    { name: "Rumelange (Luxembourg)", coords: [49.4589, 6.0294] },
    { name: "Thionville (57100)", coords: [49.3589, 6.1689] },
    { name: "Villerupt (54190)", coords: [49.4686, 5.9286] },
    { name: "Volmerange-les-Mines (57330)", coords: [49.4422, 6.0803] },
    { name: "Piennes (54490)", coords: [49.3061, 5.7831] },
    { name: "Nilvange (57240)", coords: [49.3433, 6.0503] }
];

// Mapping des codes postaux des communes du secteur
const CITY_ZIP_CODES = {
    "ottange": "57840",
    "dudelange": "3410",
    "fontoy": "57450",
    "volmerange-les-mines": "57330",
    "longwy": "54400",
    "mont-saint-martin": "54350",
    "herserange": "54440",
    "réhon": "54430",
    "lexy": "54720",
    "villers-la-montagne": "54920",
    "saulnes": "54650",
    "mercy-le-bas": "54960",
    "joppécourt": "54620",
    "boismont": "54620",
    "bazailles": "54620",
    "ville-au-montois": "54620",
    "fillières": "54560",
    "boudrezy": "54560",
    "longuyon": "54260",
    "pierrepont": "54620",
    "arrancy-sur-crusnes": "55230",
    "beuveille": "54620",
    "doncourt-lès-longuyon": "54620",
    "spincourt": "55230",
    "boulange": "57655",
    "aumetz": "57580",
    "audun-le-roman": "57565",
    "trieux": "54750",
    "hayange": "57700",
    "audun-le-tiche": "57390",
    "villerupt": "54190",
    "rumelange": "3710",
    "esch-sur-alzette": "4001",
    "hettange-grande": "57330",
    "thionville": "57100",
    "yutz": "57970",
    "cattenom": "57570",
    "roussy-le-village": "57330",
    "kanfen": "57330",
    "zoufftgen": "57330",
    "tressange": "57710",
    "havange": "57650",
    "russange": "57390",
    "rédange": "57390",
    "hussigny-godbrange": "54590",
    "tiercelet": "54860",
    "errouville": "54590",
    "crusnes": "54680",
    "serrouville": "54560",
    "brehain-la-ville": "54190",
    "differdange": "4501",
    "kayl": "3601",
    "schifflange": "3801",
    "bettembourg": "3201",
    "piennes": "54490",
    "nilvange": "57240",
    "algrange": "57440",
    "knutange": "57240",
    "florange": "57190",
    "fameck": "57290",
    "uckange": "57270",
    "serémange-erzange": "57290",
    "neufchef": "57700",
    "briey": "54150",
    "tucquegnieux": "54640",
    "bouligny": "55240",
    "joudreville": "54490"
};

// Formule de Haversine pour calculer la distance entre deux coordonnées GPS en km
function haversineDistance(coords1, coords2) {
    const [lat1, lon1] = coords1;
    const [lat2, lon2] = coords2;
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

// Formate les prix courts (ex: 450 000 € -> 450 k€)
function formatPriceShort(price) {
    if (!price) return 'N.C.';
    if (price >= 1000000) {
        return (price / 1000000).toFixed(1).replace('.0', '') + ' M€';
    }
    if (price >= 1000) {
        return (price / 1000).toFixed(0) + ' k€';
    }
    return price + ' €';
}

const MapSearch = ({ properties }) => {
    const [leafletLoaded, setLeafletLoaded] = useState(false);
    const {
        mapCenter, setMapCenter,
        mapRadius, setMapRadius,
        mapCenterName, setMapCenterName,
        mapViewportCenter, setMapViewportCenter,
        mapViewportZoom, setMapViewportZoom,
        mapApplyCityFilter, setMapApplyCityFilter,
        activeCities
    } = useSearch();

    const searchCenter = mapCenter || DEFAULT_CENTER;
    const radius = mapRadius;
    const centerName = mapCenterName || "Siège d'Ottange (57840)";

    const setSearchCenter = setMapCenter;
    const setRadius = setMapRadius;
    const setCenterName = setMapCenterName;

    // Debounced Radius logic
    const [localRadius, setLocalRadius] = useState(radius);

    // Sync localRadius when radius changes globally (e.g. from context reset)
    useEffect(() => {
        setLocalRadius(radius);
    }, [radius]);

    // Apply debounce to setMapRadius
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localRadius !== radius) {
                setMapRadius(localRadius);
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [localRadius, radius, setMapRadius]);

    const [isLocating, setIsLocating] = useState(false);
    const [isPlacingCursor, setIsPlacingCursor] = useState(false); // Mode placement curseur carte
    const isInitialMountRef = useRef(true);
    
    // References for interactive listing and distance cache
    const markersRef = useRef({});
    const distancesCache = useRef({});

    // Hover states for popup / list interactive expansion
    const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
    const [openPopupPropertyId, setOpenPopupPropertyId] = useState(null);
    const [hoverSource, setHoverSource] = useState(null); // 'map' or 'list'
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimerRef = useRef(null);

    // Clean up scroll timer on unmount
    useEffect(() => {
        return () => {
            if (scrollTimerRef.current) {
                clearTimeout(scrollTimerRef.current);
            }
        };
    }, []);

    const handleScroll = () => {
        setIsScrolling(true);
        setHoveredPropertyId(null);
        setHoverSource(null);

        if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
        }

        scrollTimerRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 200); // Réactiver les survols après 200ms de silence de scroll
    };

    // Automatically scroll the list item to the top if hovered on the map
    useEffect(() => {
        const activeId = hoveredPropertyId || openPopupPropertyId;
        if (activeId && hoverSource !== 'list') {
            const element = document.getElementById(`side-prop-${activeId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [hoveredPropertyId, openPopupPropertyId, hoverSource]);

    // Global listener to catch hover events on dynamically rendered Leaflet popup items
    useEffect(() => {
        const handleMouseEnter = (e) => {
            // Ignorer si l'utilisateur est en train de scroller
            if (isScrolling) return;

            const item = e.target.closest('.popup-item');
            if (item) {
                const propId = item.getAttribute('data-prop-id');
                setHoverSource('map');
                setHoveredPropertyId(propId);
            }
        };

        const handleMouseLeave = (e) => {
            const item = e.target.closest('.popup-item');
            if (item) {
                setHoveredPropertyId(null);
                setHoverSource(null);
            }
        };

        const handleMouseClick = (e) => {
            const item = e.target.closest('.popup-item');
            if (item) {
                const propId = item.getAttribute('data-prop-id');
                setOpenPopupPropertyId(propId);
            }
        };

        document.addEventListener('mouseover', handleMouseEnter);
        document.addEventListener('mouseout', handleMouseLeave);
        document.addEventListener('click', handleMouseClick);
        return () => {
            document.removeEventListener('mouseover', handleMouseEnter);
            document.removeEventListener('mouseout', handleMouseLeave);
            document.removeEventListener('click', handleMouseClick);
        };
    }, [isScrolling]);

    // Autocomplete & GPS State
    const [searchQuery, setSearchQuery] = useState(centerName);
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const [isSearchingGPS, setIsSearchingGPS] = useState(false);

    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersGroupRef = useRef(null);
    const centerMarkerRef = useRef(null);
    const radiusCircleRef = useRef(null);
    const autocompleteRef = useRef(null);

    // Sync input field value when centerName changes
    useEffect(() => {
        setSearchQuery(centerName);
    }, [centerName]);

    // Close autocomplete on click outside and reset query to current name
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
                setIsAutocompleteOpen(false);
                setSearchQuery(centerName);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [centerName]);

    // Helper to normalize accents and casing for robust search
    const normalizeStr = (str) => {
        if (!str) return '';
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };

    // Filter autocomplete options based on search query, searching in predefined and full coordinates
    const filteredOptions = useMemo(() => {
        const query = normalizeStr(searchQuery).trim();
        if (!query || query === normalizeStr(centerName).trim()) {
            return [{ name: "Siège d'Ottange (57840)", coords: [49.4443, 6.0195] }];
        }

        const matches = [];

        // 1. Check in predefined options first
        CENTER_OPTIONS.forEach(opt => {
            if (normalizeStr(opt.name).includes(query)) {
                matches.push(opt);
            }
        });

        // 2. Add matching cities from the full geocoding registry
        Object.keys(CITY_COORDINATES).forEach(cityName => {
            const cleanName = cityName.charAt(0).toUpperCase() + cityName.slice(1).replace(/-/g, ' ');
            const zip = CITY_ZIP_CODES[cityName] || '';
            const displayName = `${cleanName} (${zip})`;

            if (normalizeStr(cityName).includes(query) || normalizeStr(cleanName).includes(query) || zip.includes(query)) {
                // Avoid duplicates by checking coordinates or name similarity
                const alreadyExists = matches.some(m => 
                    normalizeStr(m.name).includes(normalizeStr(cityName)) ||
                    (Math.abs(m.coords[0] - CITY_COORDINATES[cityName][0]) < 0.001 &&
                     Math.abs(m.coords[1] - CITY_COORDINATES[cityName][1]) < 0.001)
                );

                if (!alreadyExists) {
                    matches.push({
                        name: displayName,
                        coords: CITY_COORDINATES[cityName]
                    });
                }
            }
        });

        return matches;
    }, [searchQuery, centerName]);

    const handleSearchGPS = async (query) => {
        if (!query || query.trim().length < 2) return;
        setIsSearchingGPS(true);
        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=3`;
            const response = await fetch(url, {
                headers: {
                    'Accept-Language': 'fr'
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data && data.length > 0) {
                    const first = data[0];
                    const lat = parseFloat(first.lat);
                    const lon = parseFloat(first.lon);
                    setSearchCenter([lat, lon]);
                    
                    const displayName = first.display_name.split(',').slice(0, 2).map(s => s.trim()).join(', ');
                    setCenterName(displayName);
                    setSearchQuery(displayName);
                    setIsAutocompleteOpen(false);
                } else {
                    alert("Aucune position GPS trouvée pour cette ville.");
                }
            } else {
                alert("Erreur lors de la recherche GPS.");
            }
        } catch (err) {
            console.error("Nominatim error:", err);
            alert("Erreur de connexion au service de recherche GPS.");
        } finally {
            setIsSearchingGPS(false);
        }
    };

    const handleClearInput = () => {
        setSearchQuery('');
        setIsAutocompleteOpen(true);
    };

    // 1. Chargement de Leaflet et du plugin MarkerCluster depuis le CDN
    useEffect(() => {
        let isMounted = true;

        const initLeaflet = async () => {
            // Ajouter les CSS de Leaflet
            if (!document.getElementById('leaflet-css')) {
                const link = document.createElement('link');
                link.id = 'leaflet-css';
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                link.crossOrigin = '';
                document.head.appendChild(link);
            }

            // Ajouter les CSS de Leaflet.markercluster
            if (!document.getElementById('leaflet-markercluster-css')) {
                const link = document.createElement('link');
                link.id = 'leaflet-markercluster-css';
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css';
                link.crossOrigin = '';
                document.head.appendChild(link);
            }
            if (!document.getElementById('leaflet-markercluster-default-css')) {
                const link = document.createElement('link');
                link.id = 'leaflet-markercluster-default-css';
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css';
                link.crossOrigin = '';
                document.head.appendChild(link);
            }

            // Ajouter le script JS de Leaflet
            if (!window.L) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.crossOrigin = '';
                script.async = true;

                const loadPromise = new Promise((resolve) => {
                    script.onload = () => resolve();
                });
                document.body.appendChild(script);
                await loadPromise;
            }

            // Ajouter le script JS de Leaflet.markercluster
            if (window.L && !window.L.markerClusterGroup) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js';
                script.crossOrigin = '';
                script.async = true;

                const loadPromise = new Promise((resolve) => {
                    script.onload = () => resolve();
                });
                document.body.appendChild(script);
                await loadPromise;
            }

            if (!isMounted) return;
            setLeafletLoaded(true);
        };

        initLeaflet();

        return () => {
            isMounted = false;
        };
    }, []);

    // 2. Initialisation de la carte
    useEffect(() => {
        if (!leafletLoaded || !mapContainerRef.current || mapInstanceRef.current) return;

        const L = window.L;

        // Utiliser le cadrage sauvegardé ou se positionner par défaut sur le centre de recherche
        const initialCenter = mapViewportCenter || searchCenter;
        const initialZoom = mapViewportZoom || 11;

        // Créer l'instance de la carte
        const map = L.map(mapContainerRef.current, {
            zoomControl: false
        }).setView(initialCenter, initialZoom);

        // Ajouter le contrôleur de zoom en haut à droite
        L.control.zoom({ position: 'topright' }).addTo(map);

        // Fond de carte Positron (très propre, moderne, épuré, parfait pour de l'immo)
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd',
            maxZoom: 20
        }).addTo(map);

        mapInstanceRef.current = map;

        // Groupe de marqueurs clusterisé Leaflet
        const markersGroup = L.markerClusterGroup({
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: function(cluster) {
                const childCount = cluster.getChildCount();
                return L.divIcon({
                    html: `
                        <div class="relative flex items-center justify-center w-10 h-10 font-sans cursor-pointer group">
                            <span class="absolute inline-flex h-full w-full rounded-full bg-[#EA723D]/30 animate-pulse opacity-75"></span>
                            <div class="relative w-8 h-8 bg-gradient-to-br from-[#EA723D] to-[#cc5825] rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white font-extrabold text-xs transition-transform duration-300 group-hover:scale-110">
                                ${childCount}
                            </div>
                        </div>
                    `,
                    className: 'custom-cluster-icon',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20]
                });
            }
        });
        markersGroup.addTo(map);
        markersGroupRef.current = markersGroup;

        // Sauvegarder la position et le zoom dans le contexte lors des déplacements
        map.on('moveend', () => {
            const center = map.getCenter();
            setMapViewportCenter([center.lat, center.lng]);
        });

        map.on('zoomend', () => {
            setMapViewportZoom(map.getZoom());
        });

        // Add contextmenu (right-click / long-press) to place cursor instantly
        map.on('contextmenu', (e) => {
            const { lat, lng } = e.latlng;
            setSearchCenter([lat, lng]);
            setCenterName("Point sélectionné sur carte");
        });

        // Reset active popup property selection when any popup is closed
        map.on('popupclose', () => {
            setOpenPopupPropertyId(null);
        });

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [leafletLoaded]);

    // 3. Liaison de l'événement clic de placement du curseur
    useEffect(() => {
        if (!leafletLoaded || !mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        const handleMapClickToPlace = (e) => {
            const { lat, lng } = e.latlng;
            setSearchCenter([lat, lng]);
            setCenterName("Point sélectionné sur carte");
            setIsPlacingCursor(false);
        };

        if (isPlacingCursor) {
            map.on('click', handleMapClickToPlace);
            if (mapContainerRef.current) {
                mapContainerRef.current.style.cursor = 'crosshair';
            }
        } else {
            if (mapContainerRef.current) {
                mapContainerRef.current.style.cursor = '';
            }
        }

        return () => {
            map.off('click', handleMapClickToPlace);
        };
    }, [leafletLoaded, isPlacingCursor]);

    // 4. Groupement des biens par coordonnées de ville pour éviter les chevauchements (avec cache de calcul de distance)
    const groupedProperties = useMemo(() => {
        const groups = {};

        properties.forEach((prop) => {
            const coords = getPropertyCoordinates(prop);
            const propId = prop.id || prop.netty_ref;
            const cacheKey = `${propId}-${searchCenter[0].toFixed(5)},${searchCenter[1].toFixed(5)}`;
            
            let distance = distancesCache.current[cacheKey];
            if (distance === undefined) {
                distance = haversineDistance(searchCenter, coords);
                distancesCache.current[cacheKey] = distance;
            }

            if (distance <= radius) {
                const key = `${coords[0].toFixed(5)},${coords[1].toFixed(5)}`;
                if (!groups[key]) {
                    groups[key] = {
                        coords,
                        distance,
                        cityName: prop.city,
                        properties: []
                    };
                }
                groups[key].properties.push(prop);
            }
        });

        return Object.values(groups).sort((a, b) => a.distance - b.distance);
    }, [properties, searchCenter, radius]);

    // Liste individuelle des biens dans le rayon
    const individualPropertiesWithinRadius = useMemo(() => {
        const list = [];
        groupedProperties.forEach((group) => {
            group.properties.forEach((prop) => {
                list.push({
                    ...prop,
                    distance: group.distance,
                    coords: group.coords
                });
            });
        });

        const getAvailabilityPriority = (p) => {
            const ms = (p.marketing_status || 'Disponible').toLowerCase();
            if (ms === 'vendu' || ms === 'loué' || ms.includes('compromis')) return -2000;
            return 0; // Disponible et Sous Offre ont la même priorité 0
        };

        return list.sort((a, b) => {
            const availDiff = getAvailabilityPriority(b) - getAvailabilityPriority(a);
            if (availDiff !== 0) return availDiff;
            return a.distance - b.distance;
        });
    }, [groupedProperties]);

    // 5. Mise à jour du centre de recherche (Marqueur glissable/draggable + Cercle)
    useEffect(() => {
        if (!leafletLoaded || !mapInstanceRef.current) return;

        const L = window.L;
        const map = mapInstanceRef.current;

        // Mettre à jour ou créer le marqueur du centre (DRAGGABLE)
        if (centerMarkerRef.current) {
            centerMarkerRef.current.setLatLng(searchCenter);
            
            // Mettre à jour l'interactivité du marqueur en fonction de isPlacingCursor
            const el = centerMarkerRef.current.getElement();
            if (el) {
                if (isPlacingCursor) {
                    el.classList.remove('pe-none');
                    el.classList.add('pe-auto');
                    centerMarkerRef.current.dragging?.enable();
                } else {
                    el.classList.remove('pe-auto');
                    el.classList.add('pe-none');
                    centerMarkerRef.current.dragging?.disable();
                }
            }
        } else {
            const centerIcon = L.divIcon({
                className: 'custom-center-marker',
                html: `
                    <div class="relative flex items-center justify-center w-6 h-6 pointer-events-none">
                        <span class="absolute inline-flex h-10 h-10 rounded-full bg-[#EA723D]/30 animate-ping opacity-75 pointer-events-none"></span>
                        <div class="relative w-5 h-5 bg-[#EA723D] rounded-full border-2 border-white shadow-lg flex items-center justify-center pointer-events-auto">
                            <div class="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                    </div>
                `,
                iconSize: [24, 24],
                iconAnchor: [12, 12]
            });

            // Définir draggable: isPlacingCursor et zIndexOffset à -50 pour ne pas gêner
            const centerMarker = L.marker(searchCenter, { 
                icon: centerIcon, 
                zIndexOffset: -50,
                draggable: isPlacingCursor 
            }).addTo(map);

            const el = centerMarker.getElement();
            if (el) {
                el.classList.add(isPlacingCursor ? 'pe-auto' : 'pe-none');
            }

            // Disable map dragging when dragging marker to make dragging smooth on touch/mobile
            const disableMapDrag = () => map.dragging.disable();
            const enableMapDrag = () => map.dragging.enable();

            centerMarker.on('mousedown', disableMapDrag);
            centerMarker.on('touchstart', disableMapDrag);
            centerMarker.on('mouseup', enableMapDrag);
            centerMarker.on('touchend', enableMapDrag);

            centerMarker.on('dragend', (e) => {
                enableMapDrag();
                const latlng = e.target.getLatLng();
                setSearchCenter([latlng.lat, latlng.lng]);
                setCenterName("Point sélectionné sur carte");
                setIsPlacingCursor(false);
            });

            centerMarkerRef.current = centerMarker;
        }

        // Mettre à jour ou créer le cercle de rayon
        if (radiusCircleRef.current) {
            radiusCircleRef.current.setLatLng(searchCenter);
            radiusCircleRef.current.setRadius(radius * 1000);
        } else {
            radiusCircleRef.current = L.circle(searchCenter, {
                radius: radius * 1000,
                color: '#EA723D',
                fillColor: '#EA723D',
                fillOpacity: 0.08,
                weight: 2,
                dashArray: '5, 8'
            }).addTo(map);
        }

        // Adapter le zoom de la carte aux limites du cercle (avec transition douce) uniquement si ce n'est pas le montage avec viewport existant
        if (isInitialMountRef.current && mapViewportCenter && mapViewportZoom !== null) {
            isInitialMountRef.current = false;
        } else {
            isInitialMountRef.current = false;
            try {
                const bounds = radiusCircleRef.current.getBounds();
                map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
            } catch (e) {
                map.setView(searchCenter, 11);
            }
        }
    }, [leafletLoaded, searchCenter, radius, isPlacingCursor]);

    // 6. Mise à jour des marqueurs groupés sur la carte
    useEffect(() => {
        if (!leafletLoaded || !mapInstanceRef.current || !markersGroupRef.current) return;

        const L = window.L;
        const markersGroup = markersGroupRef.current;

        markersGroup.clearLayers();
        markersRef.current = {}; // Vider le dictionnaire

        groupedProperties.forEach((group) => {
            const count = group.properties.length;
            const minPrice = Math.min(...group.properties.map(p => p.price || 0));

            let markerHtml = '';

            if (count === 1) {
                const prop = group.properties[0];
                markerHtml = `
                    <div class="relative group cursor-pointer pointer-events-none font-sans transition-all duration-300">
                        <div class="custom-badge px-2.5 py-1.5 rounded-lg shadow-md font-bold text-xs border border-white whitespace-nowrap transition-all duration-300 bg-[#EA723D] text-white group-hover:bg-[#2C353D] group-hover:scale-105">
                            ${formatPriceShort(prop.price)}
                        </div>
                        <div class="custom-arrow w-2.5 h-2.5 rotate-45 mx-auto -mt-1.5 border-r border-b border-white transition-all duration-300 bg-[#EA723D] group-hover:bg-[#2C353D]"></div>
                    </div>
                `;
            } else {
                markerHtml = `
                    <div class="relative group cursor-pointer pointer-events-none font-sans transition-all duration-300">
                        <div class="custom-badge px-2.5 py-1.5 rounded-lg shadow-md font-extrabold text-xs border-2 border-white whitespace-nowrap transition-all duration-300 bg-gradient-to-br from-[#EA723D] to-[#cc5825] text-white group-hover:from-[#2C353D] group-hover:to-[#1a2126] group-hover:scale-105">
                            ${formatPriceShort(minPrice)}
                            <span class="ml-1 text-[9px] px-1 py-0.2 rounded-full font-black bg-white text-[#EA723D] group-hover:bg-[#EA723D] group-hover:text-white">
                                +${count - 1}
                            </span>
                        </div>
                        <div class="custom-arrow w-2.5 h-2.5 rotate-45 mx-auto -mt-1.5 border-r border-b border-white transition-all duration-300 bg-[#cc5825] group-hover:bg-[#1a2126]"></div>
                    </div>
                `;
            }

            const propIcon = L.divIcon({
                className: 'custom-property-marker',
                html: markerHtml,
                iconSize: [85, 30],
                iconAnchor: [42, 30]
            });

            let popupHtml = '';

            if (count === 1) {
                const prop = group.properties[0];
                const routeUrl = `#/property/${prop.id || prop.netty_ref}`;
                popupHtml = `
                    <div class="popup-item w-60 font-sans p-1 text-left" data-prop-id="${prop.id || prop.netty_ref}">
                        <div class="h-28 w-full rounded-lg overflow-hidden bg-gray-100 mb-2 shadow-inner overflow-hidden relative">
                            <img src="${prop.image_url || prop.image || ''}" alt="${prop.title}" class="w-full h-full object-cover" />
                        </div>
                        <h4 class="font-bold text-xs text-[#2C353D] line-clamp-1 mb-0.5">${prop.title}</h4>
                        <p class="text-[10px] text-gray-400 font-semibold mb-1.5 flex items-center gap-1">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-[#EA723D]">
                                <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            ${prop.city} (${prop.zip_code || ''}) • <span class="text-[#EA723D]">à ${group.distance.toFixed(1)} km</span>
                        </p>
                        <div class="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                            <span class="font-black text-xs text-[#EA723D]">${formatPrice(prop.price)}</span>
                            <a href="${routeUrl}" class="bg-[#2C353D] hover:bg-[#EA723D] !text-white text-[9px] font-bold px-2.5 py-1.5 rounded-md transition-colors uppercase tracking-wider no-underline">
                                Fiche bien
                            </a>
                        </div>
                    </div>
                `;
            } else {
                popupHtml = `
                    <div class="w-64 font-sans p-1 text-left">
                        <h4 class="font-black text-[11px] text-[#2C353D] mb-2 uppercase tracking-wider border-b border-gray-100 pb-1.5 flex justify-between items-center">
                            <span>🏠 ${group.cityName}</span>
                            <span class="bg-orange-100 text-[#EA723D] px-2 py-0.5 rounded-full text-[9px] font-bold">${count} biens</span>
                        </h4>
                        <div class="max-h-56 overflow-y-auto pr-1 space-y-2 select-text">
                            ${group.properties.map(prop => `
                                <div class="popup-item flex gap-2.5 py-1.5 border-b border-gray-100 last:border-0 last:pb-0 items-center cursor-pointer hover:bg-orange-50/50 p-1 rounded-lg transition-colors" data-prop-id="${prop.id || prop.netty_ref}">
                                    <img src="${prop.image_url || prop.image || ''}" class="w-12 h-12 object-cover rounded-lg bg-gray-50 shrink-0" />
                                    <div class="flex-1 min-w-0 flex flex-col justify-between font-sans">
                                        <h5 class="font-extrabold text-[10px] text-[#2C353D] line-clamp-1 leading-tight mb-0.5">${prop.title}</h5>
                                        <p class="text-[9px] text-gray-400 font-bold uppercase tracking-wide leading-none mb-1">
                                            ${prop.type} • ${prop.surface || prop.surface_terrain || '-'}m²
                                        </p>
                                        <div class="flex items-center justify-between">
                                            <span class="font-black text-[10px] text-[#EA723D]">${formatPrice(prop.price)}</span>
                                            <a href="#/property/${prop.id || prop.netty_ref}" class="text-[9px] !text-[#2C353D] hover:!text-[#EA723D] font-extrabold underline uppercase tracking-wider no-underline">
                                                Fiche
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `;
            }

            // Décaler légèrement le marqueur vers le nord pour ne pas masquer le nom de la ville sur la carte
            const visualCoords = [group.coords[0] + 0.0025, group.coords[1]];
            const marker = L.marker(visualCoords, { icon: propIcon })
                .bindPopup(popupHtml, {
                    maxWidth: 280,
                    className: 'custom-leaflet-popup'
                });

            // Associer le marqueur à chaque propriété du groupe pour l'interaction liste
            group.properties.forEach((prop) => {
                markersRef.current[prop.id || prop.netty_ref] = marker;
            });

            // Bind mouse events and selection state on single property markers
            if (count === 1) {
                const prop = group.properties[0];
                marker.on('mouseover', () => {
                    if (isScrolling) return;
                    setHoverSource('map');
                    setHoveredPropertyId(prop.id || prop.netty_ref);
                });
                marker.on('mouseout', () => {
                    setHoveredPropertyId(null);
                    setHoverSource(null);
                });
                marker.on('popupopen', () => {
                    setOpenPopupPropertyId(prop.id || prop.netty_ref);
                });
            }

            marker.on('popupopen', () => {
                const el = marker.getElement();
                if (el) {
                    const badge = el.querySelector('.custom-badge');
                    if (badge) {
                        badge.classList.remove('bg-[#EA723D]', 'from-[#EA723D]', 'to-[#cc5825]');
                        badge.classList.add('bg-[#2C353D]', 'from-[#2C353D]', 'to-[#1a2126]');
                    }
                    const arrow = el.querySelector('.custom-arrow');
                    if (arrow) {
                        arrow.classList.remove('bg-[#EA723D]', 'bg-[#cc5825]');
                        arrow.classList.add('bg-[#2C353D]');
                    }
                }
            });

            marker.on('popupclose', () => {
                const el = marker.getElement();
                if (el) {
                    const badge = el.querySelector('.custom-badge');
                    if (badge) {
                        badge.classList.remove('bg-[#2C353D]', 'from-[#2C353D]', 'to-[#1a2126]');
                        if (count === 1) {
                            badge.classList.add('bg-[#EA723D]');
                        } else {
                            badge.classList.add('from-[#EA723D]', 'to-[#cc5825]');
                        }
                    }
                    const arrow = el.querySelector('.custom-arrow');
                    if (arrow) {
                        arrow.classList.remove('bg-[#2C353D]');
                        if (count === 1) {
                            arrow.classList.add('bg-[#EA723D]');
                        } else {
                            arrow.classList.add('bg-[#cc5825]');
                        }
                    }
                }
            });

            markersGroup.addLayer(marker);
        });
    }, [leafletLoaded, groupedProperties]);

    // Fonctions d'interaction synchronisée entre la liste et les marqueurs de la carte
    const handleHighlightMarker = (prop) => {
        const marker = markersRef.current[prop.id || prop.netty_ref];
        if (marker) {
            marker.openPopup();
            const el = marker.getElement();
            if (el) {
                const badge = el.querySelector('.custom-badge');
                if (badge) {
                    badge.classList.remove('bg-[#EA723D]', 'from-[#EA723D]', 'to-[#cc5825]');
                    badge.classList.add('bg-[#2C353D]', 'from-[#2C353D]', 'to-[#1a2126]', 'scale-110');
                }
                const arrow = el.querySelector('.custom-arrow');
                if (arrow) {
                    arrow.classList.remove('bg-[#EA723D]', 'bg-[#cc5825]');
                    arrow.classList.add('bg-[#2C353D]');
                }
            }
        }
    };

    const handleUnhighlightMarker = (prop) => {
        const marker = markersRef.current[prop.id || prop.netty_ref];
        if (marker) {
            marker.closePopup();
            const el = marker.getElement();
            if (el) {
                const badge = el.querySelector('.custom-badge');
                if (badge) {
                    badge.classList.remove('bg-[#2C353D]', 'from-[#2C353D]', 'to-[#1a2126]', 'scale-110');
                }
            }
        }
    };

    const handleFocusMarker = (prop) => {
        const marker = markersRef.current[prop.id || prop.netty_ref];
        const map = mapInstanceRef.current;
        if (map && marker) {
            const coords = getPropertyCoordinates(prop);
            const focusCoords = [coords[0] + 0.003, coords[1]];
            map.setView(focusCoords, 14, { animate: true, duration: 0.5 });
            marker.openPopup();
        }
    };

    // 7. Centrer sur la position GPS de l'utilisateur
    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            alert("La géolocalisation n'est pas supportée par votre navigateur.");
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coords = [position.coords.latitude, position.coords.longitude];
                setSearchCenter(coords);
                setCenterName("Ma position GPS");
                setIsLocating(false);
            },
            (error) => {
                console.warn("Geolocation error:", error);
                alert("Impossible de récupérer votre position GPS. Veuillez sélectionner une ville dans la liste.");
                setIsLocating(false);
            },
            { enableHighAccuracy: true, timeout: 6000 }
        );
    };

    // Liste triée pour le panneau de droite : place le bien survolé au sommet si le survol vient de la carte (évite le jitter)
    const sortedPropertiesForList = useMemo(() => {
        if (hoveredPropertyId && hoverSource === 'map') {
            const hoveredProp = individualPropertiesWithinRadius.find(
                p => String(p.id || p.netty_ref) === String(hoveredPropertyId)
            );
            if (hoveredProp) {
                const remaining = individualPropertiesWithinRadius.filter(
                    p => String(p.id || p.netty_ref) !== String(hoveredPropertyId)
                );
                return [hoveredProp, ...remaining];
            }
        }
        return individualPropertiesWithinRadius;
    }, [individualPropertiesWithinRadius, hoveredPropertyId, hoverSource]);

    return (
        <div className="w-full flex flex-col space-y-6">
            {/* Style CSS personnalisé pour Leaflet injecté localement */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .leaflet-popup-content-wrapper {
                    border-radius: 1rem !important;
                    padding: 0.25rem !important;
                    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1) !important;
                    border: 1px solid #F3F4F6 !important;
                }
                .leaflet-popup-content {
                    margin: 8px 8px !important;
                }
                .leaflet-popup-tip-container {
                    display: none !important;
                }
                .custom-property-marker {
                    overflow: visible !important;
                    pointer-events: auto !important;
                }
                .custom-center-marker {
                    overflow: visible !important;
                }
                .custom-center-marker.pe-none {
                    pointer-events: none !important;
                }
                .custom-center-marker.pe-auto {
                    pointer-events: auto !important;
                }
                /* Forcer le soulignement et la couleur des liens Leaflet */
                .custom-leaflet-popup a {
                    text-decoration: none !important;
                }
                /* Scale the wrapper instead of the root popup to not break Leaflet's translate3d positioning */
                .custom-leaflet-popup {
                    pointer-events: auto !important;
                    transition: none !important; /* Neutralise tout tremblement ou déplacement intempestif */
                }
                .custom-leaflet-popup .leaflet-popup-content-wrapper {
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    transform-origin: bottom center;
                }
                .custom-leaflet-popup:hover .leaflet-popup-content-wrapper {
                    transform: scale(1.08) !important; /* S'agrandit légèrement au survol */
                    box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25) !important;
                    z-index: 1000 !important;
                }
                .custom-leaflet-popup img {
                    transition: transform 0.5s ease !important;
                }
                .custom-leaflet-popup:hover img {
                    transform: scale(1.06) !important;
                }

                /* Custom styling for range input range thumb to make dragging smooth and reliable */
                input[type="range"] {
                    -webkit-appearance: none;
                    appearance: none;
                    background: #E5E7EB !important; /* gray-200 */
                    outline: none;
                }
                input[type="range"]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #EA723D !important;
                    cursor: grab;
                    border: 3px solid #ffffff !important;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                    transition: transform 0.1s ease, background-color 0.1s ease;
                }
                input[type="range"]::-webkit-slider-thumb:active {
                    cursor: grabbing;
                    transform: scale(1.25);
                }
                input[type="range"]::-moz-range-thumb {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #EA723D !important;
                    cursor: grab;
                    border: 3px solid #ffffff !important;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                    transition: transform 0.1s ease, background-color 0.1s ease;
                }
                input[type="range"]::-moz-range-thumb:active {
                    cursor: grabbing;
                    transform: scale(1.25);
                }

                /* Custom scrollbar styling for side panel results */
                .custom-scrollbar::-webkit-scrollbar {
                    width: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #E5E7EB;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #D1D5DB;
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: #E5E7EB transparent;
                }
            `}} />

            {/* Conteneur principal - Carte 100% avec panneau de contrôle positionné au-dessus */}
            <div className="flex flex-col gap-6 bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-gray-100 min-h-[600px] w-full relative">

                {/* Panneau de Contrôle Horizontal - Positionné au-dessus de la carte */}
                <div className="bg-[#F8F9FA] p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col lg:flex-row lg:flex-wrap xl:flex-nowrap items-stretch lg:items-center gap-6 justify-between">
                    
                    {/* Partie 1: Titre & Stats */}
                    <div className="flex items-center gap-3 border-b lg:border-b-0 lg:border-r border-gray-200 pb-4 lg:pb-0 lg:pr-6 shrink-0 text-left">
                        <div className="bg-[#EA723D] text-white p-2.5 rounded-xl">
                            <Compass size={20} />
                        </div>
                        <div>
                            <h3 className="font-extrabold text-sm uppercase tracking-wider text-[#2C353D]">Recherche par Rayon</h3>
                            <p className="text-xs font-bold text-gray-400 mt-0.5">{individualPropertiesWithinRadius.length} biens dans la zone</p>
                        </div>
                    </div>

                    {/* Partie 2: Autocomplete de recherche de Ville */}
                    <div className="flex-1 min-w-[220px] space-y-1 text-left relative" ref={autocompleteRef}>
                        <label htmlFor="center-search-input" className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block ml-1">
                            Centre de recherche
                        </label>
                        <div className="flex gap-2 mt-1">
                            <div className="relative flex-1 flex items-center">
                                <input
                                    id="center-search-input"
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setIsAutocompleteOpen(true);
                                    }}
                                    onFocus={() => setIsAutocompleteOpen(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const query = normalizeStr(searchQuery).trim();
                                            if (query) {
                                                const exactLocalMatch = filteredOptions.find(opt => 
                                                    normalizeStr(opt.name) === query || 
                                                    normalizeStr(opt.name).replace(" (secteur)", "") === query
                                                );
                                                if (exactLocalMatch) {
                                                    setSearchCenter(exactLocalMatch.coords);
                                                    setCenterName(exactLocalMatch.name);
                                                    setSearchQuery(exactLocalMatch.name);
                                                    setIsAutocompleteOpen(false);
                                                } else if (filteredOptions.length > 0) {
                                                    const bestMatch = filteredOptions[0];
                                                    setSearchCenter(bestMatch.coords);
                                                    setCenterName(bestMatch.name);
                                                    setSearchQuery(bestMatch.name);
                                                    setIsAutocompleteOpen(false);
                                                } else {
                                                    handleSearchGPS(searchQuery);
                                                }
                                            }
                                        }
                                    }}
                                    placeholder="Saisir ou choisir une ville..."
                                    className="w-full bg-white border border-gray-200 text-gray-800 text-xs rounded-xl focus:ring-2 focus:ring-[#EA723D]/20 focus:border-[#EA723D] p-3 pr-8 font-semibold outline-none transition-all shadow-sm"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={handleClearInput}
                                        type="button"
                                        className="absolute right-2.5 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                                {isAutocompleteOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl z-[900] max-h-48 overflow-y-auto divide-y divide-gray-100 font-sans">
                                        <button
                                            onClick={() => {
                                                handleGetLocation();
                                                setIsAutocompleteOpen(false);
                                            }}
                                            className="w-full text-left p-3 hover:bg-orange-50/50 text-[#EA723D] font-extrabold text-xs border-b border-gray-50 flex items-center gap-1.5 cursor-pointer"
                                        >
                                            📍 Ma Position (GPS)
                                        </button>
                                        
                                        {filteredOptions.length > 0 ? (
                                            filteredOptions.slice(0, 10).map((opt) => (
                                                <button
                                                    key={opt.name}
                                                    onClick={() => {
                                                        setSearchCenter(opt.coords);
                                                        setCenterName(opt.name);
                                                        setSearchQuery(opt.name);
                                                        setIsAutocompleteOpen(false);
                                                    }}
                                                    className="w-full text-left p-3 hover:bg-gray-50 text-gray-700 font-semibold text-xs border-b border-gray-50 last:border-0 cursor-pointer flex items-center gap-2"
                                                >
                                                    <MapPin size={12} className="text-gray-400 shrink-0" />
                                                    <span>{opt.name}</span>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="p-3 text-xs text-gray-400 font-semibold italic text-center">
                                                Aucune commune locale correspondante
                                            </div>
                                        )}

                                        {searchQuery && searchQuery.trim().length >= 2 && (
                                            <button
                                                onClick={() => handleSearchGPS(searchQuery)}
                                                disabled={isSearchingGPS}
                                                className="w-full text-left p-3 bg-gray-50 hover:bg-orange-50 hover:text-[#EA723D] transition-colors flex items-center gap-2 font-bold text-xs text-[#EA723D] border-t border-gray-100 cursor-pointer"
                                            >
                                                <Search size={12} className={`shrink-0 ${isSearchingGPS ? 'animate-spin' : ''}`} />
                                                <span>
                                                    {isSearchingGPS ? 'Recherche GPS en cours...' : `Rechercher "${searchQuery}" en ligne (GPS)`}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
 
                            <button
                                onClick={handleGetLocation}
                                disabled={isLocating}
                                title="Ma position GPS"
                                className="p-3 bg-white border border-gray-200 hover:border-[#EA723D] hover:text-[#EA723D] rounded-xl text-gray-500 transition-colors flex items-center justify-center disabled:opacity-50 shrink-0 cursor-pointer"
                            >
                                <Navigation className={`w-4 h-4 ${isLocating ? 'animate-spin text-[#EA723D]' : ''}`} />
                            </button>
                        </div>
                    </div>

                    {/* Partie 3: Curseur Interactif de position sur carte */}
                    <div className="flex-1 min-w-[200px] space-y-1 text-left">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block ml-1">
                            Placer le repère sur carte
                        </label>
                        <button
                            onClick={() => setIsPlacingCursor(!isPlacingCursor)}
                            className={`w-full p-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                isPlacingCursor 
                                    ? 'bg-orange-50 border-[#EA723D] text-[#EA723D] animate-pulse' 
                                    : centerName === "Point sélectionné sur carte"
                                        ? 'bg-white border-[#EA723D]/50 text-[#EA723D] hover:bg-gray-50'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800'
                            }`}
                        >
                            <MapPin size={14} className={isPlacingCursor ? 'text-[#EA723D]' : 'text-gray-400'} />
                            <span>
                                {isPlacingCursor 
                                    ? "Cliquez sur la carte..." 
                                    : centerName === "Point sélectionné sur carte"
                                        ? "Repositionner le curseur"
                                        : "Définir un point sur la carte"
                                }
                            </span>
                        </button>
                    </div>

                    {/* Partie 3.5: Optionnel - Filtrage par villes sélectionnées dans le Hero */}
                    {activeCities && activeCities.length > 0 && (
                        <div className="flex-1 min-w-[180px] space-y-1 text-left">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block ml-1">
                                Filtre de localisation
                            </label>
                            <label className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors shadow-sm select-none h-[42px] mt-1">
                                <input
                                    type="checkbox"
                                    checked={mapApplyCityFilter}
                                    onChange={(e) => setMapApplyCityFilter(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-[#EA723D] focus:ring-[#EA723D]/20 cursor-pointer"
                                />
                                <span className="text-xs font-black text-gray-700 leading-none">
                                    Filtrer par villes ({activeCities.length})
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Partie 4: Curseurs de Rayon (sans transition-all ni touch-none pour corriger le drag) */}
                    <div className="flex-1 min-w-[200px] space-y-1 text-left">
                        <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-wider text-gray-400 px-1">
                            <span>Distance</span>
                            <span className="text-[#EA723D] font-extrabold text-xs lowercase">{localRadius} km</span>
                        </div>
                        <input
                            type="range"
                            min="2"
                            max="50"
                            step="1"
                            value={localRadius}
                            onChange={(e) => setLocalRadius(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#EA723D]"
                        />
                        <div className="flex justify-between text-[8px] text-gray-400 font-bold px-1">
                            <span>2 km</span>
                            <span>25 km</span>
                            <span>50 km</span>
                        </div>
                    </div>
                </div>

                {/* Zone interactive Split-Screen (Carte à gauche, Liste à droite sur Desktop) */}
                <div className="flex flex-col lg:flex-row gap-6 w-full items-stretch flex-1">
                    
                    {/* Zone de la carte interactive */}
                    <div className="w-full lg:flex-1 h-[450px] lg:h-[550px] shrink-0 rounded-2xl overflow-hidden relative shadow-inner bg-gray-50 border border-gray-100 z-0">
                        
                        {/* Bouton de ciblage sur carte (positionné sous les boutons zoom +- de Leaflet) - DRAGGABLE onto the map */}
                        {leafletLoaded && (
                            <button
                                draggable="true"
                                onDragStart={(e) => {
                                    e.dataTransfer.setData("text/plain", "map-pin");
                                    e.dataTransfer.effectAllowed = "move";
                                }}
                                onClick={() => setIsPlacingCursor(!isPlacingCursor)}
                                className={`absolute top-[82px] right-3.5 w-[30px] h-[30px] z-[650] rounded border shadow-md flex items-center justify-center transition-all cursor-pointer pointer-events-auto active:scale-95 ${
                                    isPlacingCursor 
                                        ? 'bg-[#EA723D] border-[#EA723D] text-white animate-pulse shadow-orange-500/20' 
                                        : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-600 hover:text-[#EA723D]'
                                }`}
                                title="Glissez ce bouton sur la carte pour déplacer le curseur, ou cliquez dessus pour l'activer"
                            >
                                <MapPin size={15} />
                            </button>
                        )}

                        {/* Bouton flottant de recentrage */}
                        {leafletLoaded && (
                            <button
                                onClick={() => {
                                    const map = mapInstanceRef.current;
                                    const circle = radiusCircleRef.current;
                                    if (map && circle) {
                                        try {
                                            const bounds = circle.getBounds();
                                            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
                                        } catch (e) {
                                            map.setView(searchCenter, 11);
                                        }
                                    }
                                }}
                                className="absolute bottom-5 right-5 w-10 h-10 z-[650] rounded-full bg-white border border-gray-200 shadow-xl flex items-center justify-center text-[#2C353D] hover:text-[#EA723D] hover:scale-110 active:scale-95 transition-all duration-300 cursor-pointer pointer-events-auto"
                                title="Recentrer sur la zone de recherche"
                            >
                                <Compass size={18} />
                            </button>
                        )}

                        {/* Indication visuelle lorsque le mode placement de curseur est actif */}
                        {leafletLoaded && isPlacingCursor && (
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-[#EA723D] text-white px-5 py-3 rounded-full shadow-2xl z-[650] text-xs font-extrabold flex items-center gap-2 animate-bounce pointer-events-none">
                                <MapPin size={14} className="animate-pulse" />
                                <span>Cliquez n'importe où sur la carte pour définir le centre de recherche</span>
                            </div>
                        )}

                        {/* La div de la carte Leaflet (sans z-index bloquant pour les popups) */}
                        <div 
                            id="leaflet-map" 
                            ref={mapContainerRef} 
                            className="w-full h-full" 
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const map = mapInstanceRef.current;
                                if (!map) return;
                                
                                const rect = mapContainerRef.current.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                
                                const latlng = map.containerPointToLatLng([x, y]);
                                setSearchCenter([latlng.lat, latlng.lng]);
                                setCenterName("Point sélectionné sur carte");
                            }}
                        />
                    </div>

                    {/* Panneau latéral de résultats (Desktop uniquement en Split view, s'affiche en-dessous sur mobile) */}
                    <div className="w-full lg:w-[320px] xl:w-[380px] bg-[#F8F9FA] rounded-2xl border border-gray-200 p-4 flex flex-col h-[350px] lg:h-[550px] shrink-0 text-left">
                        <h4 className="font-extrabold text-[10px] text-[#2C353D] uppercase tracking-wider mb-3 flex justify-between items-center px-1">
                            <span>Biens trouvés dans la zone</span>
                            <span className="bg-[#EA723D]/10 text-[#EA723D] px-2.5 py-0.5 rounded-full text-[10px] font-black">
                                {individualPropertiesWithinRadius.length}
                            </span>
                        </h4>

                        {individualPropertiesWithinRadius.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-gray-400">
                                <MapPin size={28} className="text-gray-300 mb-2 stroke-[1.5]" />
                                <p className="font-extrabold text-xs uppercase tracking-wide">Aucun bien trouvé</p>
                                <p className="text-[10px] text-gray-400 mt-1 max-w-[200px] mx-auto leading-relaxed">
                                    Ajustez la position du curseur ou augmentez le rayon de recherche.
                                </p>
                                <button
                                    onClick={() => setLocalRadius(prev => Math.min(prev + 10, 50))}
                                    className="mt-4 px-4 py-2 bg-[#EA723D] hover:bg-[#2C353D] text-white text-[10px] font-extrabold uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-95 cursor-pointer"
                                >
                                    +10 km de rayon
                                </button>
                            </div>
                        ) : (
                            <div 
                                onScroll={handleScroll}
                                className="flex-1 overflow-y-auto pr-1 space-y-3 custom-scrollbar"
                            >
                                {sortedPropertiesForList.map((prop) => {
                                    const isExpanded = String(prop.id || prop.netty_ref) === String(hoveredPropertyId) || String(prop.id || prop.netty_ref) === String(openPopupPropertyId);
                                    return (
                                        <div
                                            id={`side-prop-${prop.id || prop.netty_ref}`}
                                            key={prop.id || prop.netty_ref}
                                            onMouseEnter={() => {
                                                if (isScrolling) return;
                                                setHoverSource('list');
                                                setHoveredPropertyId(prop.id || prop.netty_ref);
                                                handleHighlightMarker(prop);
                                            }}
                                            onMouseLeave={() => {
                                                setHoveredPropertyId(null);
                                                setHoverSource(null);
                                                handleUnhighlightMarker(prop);
                                            }}
                                            onClick={() => handleFocusMarker(prop)}
                                            className={`bg-white rounded-xl border transition-all duration-500 flex cursor-pointer group relative text-left overflow-hidden ${
                                                isExpanded 
                                                    ? 'p-4 border-[#EA723D] shadow-lg shadow-orange-500/10 flex-col gap-4 scale-[1.02] z-10' 
                                                    : 'p-3 border-gray-100 hover:border-[#EA723D]/50 hover:shadow-md flex-row gap-3'
                                            }`}
                                        >
                                            {/* Image du bien */}
                                            <div className={`rounded-lg overflow-hidden bg-gray-50 shrink-0 relative shadow-inner transition-all duration-500 ${
                                                isExpanded ? 'w-full h-40' : 'w-16 h-16'
                                            }`}>
                                                <img 
                                                    src={prop.image_url || prop.image || ''} 
                                                    alt={prop.title} 
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                                                />
                                            </div>

                                            {/* Détails du bien */}
                                            <div className="flex-1 min-w-0 flex flex-col justify-between font-sans transition-all duration-500">
                                                <div>
                                                    <h5 className={`font-extrabold text-[#2C353D] group-hover:text-[#EA723D] transition-colors leading-tight mb-1 ${
                                                        isExpanded ? 'text-sm' : 'text-xs line-clamp-1'
                                                    }`}>
                                                        {prop.title}
                                                    </h5>
                                                    <p className={`text-[#EA723D] font-black leading-none mb-1.5 ${
                                                        isExpanded ? 'text-base' : 'text-xs hidden'
                                                    }`}>
                                                        {formatPrice(prop.price)}
                                                    </p>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wide leading-none">
                                                        {prop.type} • {prop.city} {isExpanded && prop.zip_code ? `(${prop.zip_code})` : ''}
                                                    </p>
                                                    {isExpanded && (
                                                        <p className="text-[10px] text-gray-500 mt-2 line-clamp-3 leading-normal font-medium">
                                                            {prop.description || "Aucune description supplémentaire disponible."}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className={`flex items-center justify-between border-t border-gray-50 pt-2 mt-2 ${
                                                    isExpanded ? 'w-full' : ''
                                                }`}>
                                                    {!isExpanded && <span className="font-black text-xs text-[#EA723D]">{formatPrice(prop.price)}</span>}
                                                    {isExpanded && (
                                                        <span className="text-[10px] font-black text-gray-700 bg-gray-100 px-2.5 py-1 rounded-md">
                                                            📐 {prop.surface || prop.surface_terrain || '-'} m²
                                                        </span>
                                                    )}
                                                    <span className="text-[9px] font-bold text-gray-400 shrink-0">
                                                        à {prop.distance.toFixed(1)} km
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Lien Fiche direct */}
                                            <a
                                                href={`#/property/${prop.id || prop.netty_ref}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className={`absolute top-3 right-3 transition-all duration-300 bg-[#2C353D] hover:bg-[#EA723D] text-white rounded-lg ${
                                                    isExpanded ? 'opacity-100 p-2' : 'opacity-0 group-hover:opacity-100 p-1'
                                                }`}
                                                title="Voir la fiche détaillée"
                                            >
                                                <ChevronRight size={isExpanded ? 16 : 12} />
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapSearch;
